
import Integer from './Integer.js'
import { MILLION, THOUSAND } from './constants.js'
import { rejectDuration } from './regulate.js'
import DateTime from '../DateTime.js'
import PlainDate from '../PlainDate.js'
import PlainTime from '../PlainTime.js'
import Duration from '../Duration.js'
import ObjectCache from './ObjectCache.js'
import Calendar from '../Calendar.js'
import TimeZone from '../TimeZone.js'

const tzComponent =
  /\.[-A-Za-z_]|\.\.[-A-Za-z._]{1,12}|\.[-A-Za-z_][-A-Za-z._]{0,12}|[A-Za-z_][-A-Za-z._]{0,13}/
const offsetNoCapture =
  /(?:[+\u2212-][0-2][0-9](?::?[0-5][0-9](?::?[0-5][0-9](?:[.,]\d{1,9})?)?)?)/
export const timeZoneID = new RegExp(
  '(?:' +
    [
      `(?:${tzComponent.source})(?:\\/(?:${tzComponent.source}))*`,
      'Etc/GMT(?:0|[-+]\\d{1,2})',
      'GMT[-+]?0',
      'EST5EDT',
      'CST6CDT',
      'MST7MDT',
      'PST8PDT',
      offsetNoCapture.source
    ].join('|') +
    ')'
)
const calComponent = /[A-Za-z0-9]{3,8}/
const calendarID = new RegExp(
  `(?:${calComponent.source}(?:-${calComponent.source})*)`
)

const yearpart = /(?:[+\u2212-]\d{6}|\d{4})/
const monthpart = /(?:0[1-9]|1[0-2])/
const daypart = /(?:0[1-9]|[12]\d|3[01])/
const datesplit = new RegExp(
  `(${yearpart.source})(?:-(${monthpart.source})-(${daypart.source})|(${monthpart.source})(${daypart.source}))`
)
const timesplit =
  /(\d{2})(?::(\d{2})(?::(\d{2})(?:[.,](\d{1,9}))?)?|(\d{2})(?:(\d{2})(?:[.,](\d{1,9}))?)?)?/
export const offset =
  /([+\u2212-])([01][0-9]|2[0-3])(?::?([0-5][0-9])(?::?([0-5][0-9])(?:[.,](\d{1,9}))?)?)?/
const zonesplit = new RegExp(
  `(?:([zZ])|(?:${offset.source})?)(?:\\[(${timeZoneID.source})\\])?`
)
const calendar = new RegExp(`\\[u-ca=(${calendarID.source})\\]`)

export const ZONED_DATE_TIME_REGEX = new RegExp(
  `^${datesplit.source}(?:(?:T|\\s+)${timesplit.source})?${zonesplit.source}(?:${calendar.source})?$`,
  'i'
)

export const PLAIN_DATE_REGEX = new RegExp(
  `^${datesplit.source}(?:${calendar.source})?$`,
  'i'
)

const time = new RegExp(`^T?${timesplit.source}(?:${zonesplit.source})?(?:${calendar.source})?$`, 'i')
const yearmonth = new RegExp(`^(${yearpart.source})-?(${monthpart.source})$`)
const monthday = new RegExp(`^(?:--)?(${monthpart.source})-?(${daypart.source})$`)
const fraction = /(\d+)(?:[.,](\d{1,9}))?/
const durationDate = /(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?/
const durationTime = new RegExp(`(?:${fraction.source}H)?(?:${fraction.source}M)?(?:${fraction.source}S)?`)
const DURATION_REGEX = new RegExp(`^([+\u2212-])?P${durationDate.source}(?:T(?!$)${durationTime.source})?$`, 'i')

function fromMatchesDateTime (value, matches) {
  let yearString = matches[1]
  if (yearString[0] === '\u2212') yearString = `-${yearString.slice(1)}`
  if (yearString === '-000000') throw new RangeError(`invalid ISO 8601 string: ${value}`)
  const year = Integer(yearString)
  const month = Integer(matches[2] || matches[4])
  const day = Integer(matches[3] || matches[5])
  const hour = Integer(matches[6])
  const minute = Integer(matches[7] || matches[10])
  let second = Integer(matches[8] || matches[11])
  if (second === 60) second = 59
  const fraction = (matches[9] || matches[12]) + '000000000'
  const millisecond = Integer(fraction.slice(0, 3))
  const microsecond = Integer(fraction.slice(3, 6))
  const nanosecond = Integer(fraction.slice(6, 9))
  const ianaName = matches[19]
  const calendar = matches[20]

  return new DateTime(
    year,
    month,
    day,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond,
    ianaName,
    calendar
  )
}

function fromMatchesDate (value, matches) {
  let yearString = matches[1]
  if (yearString[0] === '\u2212') yearString = `-${yearString.slice(1)}`
  if (yearString === '-000000') throw new RangeError(`invalid ISO 8601 string: ${value}`)

  return new PlainDate(
    Integer(yearString),
    Integer(matches[2] || matches[4]),
    Integer(matches[3] || matches[5]),
    matches[6]
  )
}

function fromMatchesTime (value, matches) {
  const hour = Integer(matches[1])
  const minute = Integer(matches[2] || matches[5])
  let second = Integer(matches[3] || matches[6])
  if (second === 60) second = 59
  const fraction = (matches[4] || matches[7]) + '000000000'
  const millisecond = Integer(fraction.slice(0, 3))
  const microsecond = Integer(fraction.slice(3, 6))
  const nanosecond = Integer(fraction.slice(6, 9))
  const calendar = matches[15]
  if (matches[8]) throw new RangeError('Z designator not supported for PlainTime')
  // if it's a date-time string, OK
  if (/[tT ][0-9][0-9]/.test(value)) {
    return new PlainTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      calendar
    )
  }

  const isoStringWithoutCalendar = calendar
    ? value.slice(0, value.length - calendar.length - 7)
    : value
  try {
    const { month, day } = ParseTemporalMonthDayString(isoStringWithoutCalendar)
  } catch {
    try {
      const { year, month } = ParseTemporalYearMonthString(isoStringWithoutCalendar)
    } catch {
      return new PlainTime(hour, minute, second, millisecond, microsecond, nanosecond, calendar)
    }
  }
  throw new RangeError(`invalid ISO 8601 time-only string ${value}; may need a T prefix`)
}

function fromMatchesDuration (value, matches) {
  if (matches.slice(2).every(element => element === undefined)) {
    throw new RangeError(`invalid duration: ${value}`)
  }

  const sign = matches[1] === '-' || matches[1] === '\u2212' ? -1 : 1
  const years = Integer(matches[2]) * sign
  const months = Integer(matches[3]) * sign
  const weeks = Integer(matches[4]) * sign
  const days = Integer(matches[5]) * sign
  const hours = Integer(matches[6]) * sign
  const fHours = matches[7]
  const minutesStr = matches[8]
  const fMinutes = matches[9]
  const secondsStr = matches[10]
  const fSeconds = matches[11]
  let minutes = 0
  let seconds = 0
  // fractional hours, minutes, or seconds, expressed in whole nanoseconds:
  let excessNanoseconds = 0

  if (fHours !== undefined) {
    if (minutesStr ?? fMinutes ?? secondsStr ?? fSeconds ?? false) {
      throw new RangeError('only the smallest unit can be fractional')
    }

    excessNanoseconds = Integer((fHours + '000000000').slice(0, 9)) * 3600 * sign
  } else {
    minutes = Integer(minutesStr) * sign

    if (fMinutes !== undefined) {
      if (secondsStr ?? fSeconds ?? false) {
        throw new RangeError('only the smallest unit can be fractional')
      }

      excessNanoseconds = Integer((fMinutes + '000000000').slice(0, 9)) * 60 * sign
    } else {
      seconds = Integer(secondsStr) * sign

      if (fSeconds !== undefined) {
        excessNanoseconds = Integer((fSeconds + '000000000').slice(0, 9)) * sign
      }
    }
  }

  const nanoseconds = excessNanoseconds % 1000
  const microseconds = Math.trunc(excessNanoseconds / 1000) % 1000
  const milliseconds = Math.trunc(excessNanoseconds / 1e6) % 1000
  seconds += Math.trunc(excessNanoseconds / 1e9) % 60
  minutes += Math.trunc(excessNanoseconds / 6e10)

  rejectDuration(
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )

  return new Duration(
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )
}

function reviverValue (key, value) {
  if (typeof value !== 'string') {
    return value
  }

  let matches = ZONED_DATE_TIME_REGEX.exec(value)

  if (matches && matches.filter(m => m !== undefined).length >= 7) {
    return fromMatchesDateTime(value, matches)
  } else if ((matches = PLAIN_DATE_REGEX.exec(value))) {
    return fromMatchesDate(value, matches)
  } else if ((matches = time.exec(value))) {
    return fromMatchesTime(value, matches)
  } else if ((matches = DURATION_REGEX.exec(value))) {
    return fromMatchesDuration(value, matches)
  } else if (Calendar.getPossibleIdentifiers().includes(value)) {
    return Calendar.from(value)
  } else if (TimeZone.getCanonicalIdentifier(value)) {
    return TimeZone.from(value)
  }

  return value
}

function reviverKey (key, value) {
  if (typeof value !== 'string') {
    return value
  }

  if (key.endsWith('DateTime')) {
    return DateTime.from(value)
  } else if (key.endsWith('Date')) {
    return PlainDate.from(value)
  } else if (key.endsWith('Time')) {
    return PlainTime.from(value)
  } else if (key.endsWith('Duration')) {
    return Duration.from(value)
  } else if (key.endsWith('TimeZone')) {
    return TimeZone.from(value)
  } else if (key.endsWith('Calendar')) {
    return Calendar.from(value)
  } else {
    return value
  }
}

function reviveKeyAndValue (key, value) {
  if (typeof value !== 'string') {
    return value
  }

  let matches = null

  if (key.endsWith('DateTime') && (matches = ZONED_DATE_TIME_REGEX.exec(value))) {
    return fromMatchesDateTime(value, matches)
  } else if (key.endsWith('Date') && (matches = PLAIN_DATE_REGEX.exec(value))) {
    return fromMatchesDate(value, matches)
  } else if (key.endsWith('Time') && (matches = time.exec(value))) {
    return fromMatchesTime(value, matches)
  } else if (key.endsWith('Duration') && (matches = DURATION_REGEX.exec(value))) {
    return fromMatchesDuration(value, matches)
  } else if (key.endsWith('TimeZone') && TimeZone.getCanonicalIdentifier(value)) {
    return TimeZone.from(value)
  } else if (key.endsWith('Calendar') && Calendar.getPossibleIdentifiers().includes(value)) {
    return Calendar.from(value)
  }

  return value
}

export class Reviver {
  revive = reviveKeyAndValue
  type = 'key+value'

  constructor (type) {
    if (type === 'key') {
      this.type = type
      this.revive = reviverKey
    } else if (type === 'value') {
      this.type = type
      this.revive = reviverValue
    } else if (type === 'key+value') {
      this.type = type
      this.revive = reviveKeyAndValue
    }
  }
}

export function parsePlainDate (plainDateString) {
  const matches = PLAIN_DATE_REGEX.exec(plainDateString)
  if (!matches) throw new RangeError(`invalid PlainDate string: ${plainDateString}`)
  let yearString = matches[1]
  if (yearString[0] === '\u2212') yearString = `-${yearString.slice(1)}`
  if (yearString === '-000000') throw new RangeError(`invalid ISO 8601 string: ${plainDateString}`)

  return {
    year: Integer(yearString),
    month: Integer(matches[2] || matches[4]),
    day: Integer(matches[3] || matches[5]),
    calendar: matches[6]
  }
}

export function parseISODateTime (isoString) {
  // ZDT is the superset of fields for every other Temporal type
  const match = ZONED_DATE_TIME_REGEX.exec(isoString)
  if (!match) throw new RangeError(`invalid ISO 8601 string: ${isoString}`)
  let yearString = match[1]
  if (yearString[0] === '\u2212') yearString = `-${yearString.slice(1)}`
  if (yearString === '-000000') throw new RangeError(`invalid ISO 8601 string: ${isoString}`)
  const year = Integer(yearString)
  const month = Integer(match[2] || match[4])
  const day = Integer(match[3] || match[5])
  const hour = Integer(match[6])
  const hasTime = match[6] !== undefined
  const minute = Integer(match[7] || match[10])
  let second = Integer(match[8] || match[11])
  if (second === 60) second = 59
  const fraction = (match[9] || match[12]) + '000000000'
  const millisecond = Integer(fraction.slice(0, 3))
  const microsecond = Integer(fraction.slice(3, 6))
  const nanosecond = Integer(fraction.slice(6, 9))
  let offset
  let z = false
  if (match[13]) {
    offset = undefined
    z = true
  } else if (match[14] && match[15]) {
    offset = BigInt(0)
    const offsetSign = match[14] === '-' || match[14] === '\u2212' ? '-' : '+'
    const offsetHours = match[15] || '00'
    const offsetMinutes = match[16] || '00'
    const offsetSeconds = match[17] || '00'
    let offsetFraction = match[18] || '0'
    offset += BigInt(Integer(`${offsetSign}${offsetHours}`) * 60 + Integer(offsetMinutes))
    // offset = `${offsetSign}${offsetHours}:${offsetMinutes}`
    if (+offsetFraction) {
      while (offsetFraction.endsWith('0')) offsetFraction = offsetFraction.slice(0, -1)
      offsetFraction = offsetFraction + '000000000'
      offset *= BigInt(60) + BigInt(offsetSeconds)
      offset *= THOUSAND
      offset += BigInt(offsetFraction.slice(0, 3))
      offset *= THOUSAND
      offset += BigInt(offsetFraction.slice(3, 6))
      offset *= THOUSAND
      offset += BigInt(offsetFraction.slice(6, 9))
      // offset += `:${offsetSeconds}.${offsetFraction}`
    } else if (+offsetSeconds) {
      // offset += `:${offsetSeconds}`
      offset *= BigInt(60) + BigInt(offsetSeconds)
      offset *= MILLION
      offset *= THOUSAND
    } else {
      offset *= BigInt(60)
      offset *= MILLION
      offset *= THOUSAND
    }
    // if (offset === '-00:00') offset = '+00:00'
  }
  const ianaName = match[19]
  const calendar = match[20]

  return {
    year,
    month,
    day,
    hasTime,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond,
    ianaName,
    offset,
    z,
    calendar
  }
}

export function parseTimeString (isoString) {
  const match = time.exec(isoString)
  let hour, minute, second, millisecond, microsecond, nanosecond, timeZone, calendar
  if (match) {
    hour = Integer(match[1])
    minute = Integer(match[2] || match[5])
    second = Integer(match[3] || match[6])
    if (second === 60) second = 59
    const fraction = (match[4] || match[7]) + '000000000'
    millisecond = Integer(fraction.slice(0, 3))
    microsecond = Integer(fraction.slice(3, 6))
    nanosecond = Integer(fraction.slice(6, 9))
    timeZone = match[14]
    calendar = match[15]
    if (match[8]) throw new RangeError('Z designator not supported for PlainTime')
  } else {
    let z, hasTime
    ;({
      hasTime,
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      calendar,
      z
    } = parseISODateTime(isoString))
    if (!hasTime) {
      throw new RangeError(`time is missing in string: ${isoString}`)
    }

    if (z) {
      throw new RangeError('Z designator not supported for PlainTime')
    }
  }
  // if it's a date-time string, OK
  if (/[tT ][0-9][0-9]/.test(isoString)) {
    return {
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      timeZone,
      calendar
    }
  }

  // Reject strings that are ambiguous with PlainMonthDay or PlainYearMonth.
  // The calendar suffix is `[u-ca=${calendar}]`, i.e. calendar plus 7 characters,
  // and must be stripped so presence of a calendar doesn't result in interpretation
  // of otherwise ambiguous input as a time.
  const isoStringWithoutCalendar = calendar
    ? isoString.slice(0, isoString.length - calendar.length - 7)
    : isoString
  try {
    const { month, day } = ParseTemporalMonthDayString(isoStringWithoutCalendar)
  } catch {
    try {
      const { year, month } = ParseTemporalYearMonthString(isoStringWithoutCalendar)
    } catch {
      return { hour, minute, second, millisecond, microsecond, nanosecond, timeZone, calendar }
    }
  }
  throw new RangeError(`invalid ISO 8601 time-only string ${isoString}; may need a T prefix`)
}

export function ParseTemporalMonthDayString (isoString) {
  const match = monthday.exec(isoString)
  let month, day, calendar, referenceISOYear
  if (match) {
    month = Integer(match[1])
    day = Integer(match[2])
  } else {
    let z
    ;({
      month,
      day,
      calendar,
      year: referenceISOYear,
      z
    } = parseISODateTime(isoString))
    if (z) throw new RangeError('Z designator not supported for PlainMonthDay')
  }
  return { month, day, calendar, referenceISOYear }
}

export function ParseTemporalYearMonthString (isoString) {
  const match = yearmonth.exec(isoString)
  let year, month, calendar, referenceISODay

  if (match) {
    let yearString = match[1]

    if (yearString[0] === '\u2212') {
      yearString = `-${yearString.slice(1)}`
    }

    if (yearString === '-000000') {
      throw new RangeError(`invalid ISO 8601 string: ${isoString}`)
    }

    year = Integer(yearString)
    month = Integer(match[2])
    calendar = match[3]
  } else {
    let z
    ;({
      year,
      month,
      calendar,
      day: referenceISODay,
      z
    } = parseISODateTime(isoString))
    if (z) throw new RangeError('Z designator not supported for PlainYearMonth')
  }
  return { year, month, calendar, referenceISODay }
}

export function parseDurationString (isoString) {
  const match = DURATION_REGEX.exec(isoString)
  if (!match) throw new RangeError(`invalid duration: ${isoString}`)
  if (match.slice(2).every(element => element === undefined)) {
    throw new RangeError(`invalid duration: ${isoString}`)
  }
  const sign = match[1] === '-' || match[1] === '\u2212' ? -1 : 1
  const years = Integer(match[2]) * sign
  const months = Integer(match[3]) * sign
  const weeks = Integer(match[4]) * sign
  const days = Integer(match[5]) * sign
  const hours = Integer(match[6]) * sign
  const fHours = match[7]
  const minutesStr = match[8]
  const fMinutes = match[9]
  const secondsStr = match[10]
  const fSeconds = match[11]
  let minutes = 0
  let seconds = 0
  // fractional hours, minutes, or seconds, expressed in whole nanoseconds:
  let excessNanoseconds = 0

  if (fHours !== undefined) {
    if (minutesStr ?? fMinutes ?? secondsStr ?? fSeconds ?? false) {
      throw new RangeError('only the smallest unit can be fractional')
    }
    excessNanoseconds =
      Integer((fHours + '000000000').slice(0, 9)) * 3600 * sign
  } else {
    minutes = Integer(minutesStr) * sign
    if (fMinutes !== undefined) {
      if (secondsStr ?? fSeconds ?? false) {
        throw new RangeError('only the smallest unit can be fractional')
      }
      excessNanoseconds =
        Integer((fMinutes + '000000000').slice(0, 9)) * 60 * sign
    } else {
      seconds = Integer(secondsStr) * sign
      if (fSeconds !== undefined) {
        excessNanoseconds = Integer((fSeconds + '000000000').slice(0, 9)) * sign
      }
    }
  }

  const nanoseconds = excessNanoseconds % 1000
  const microseconds = Math.trunc(excessNanoseconds / 1000) % 1000
  const milliseconds = Math.trunc(excessNanoseconds / 1e6) % 1000
  seconds += Math.trunc(excessNanoseconds / 1e9) % 60
  minutes += Math.trunc(excessNanoseconds / 6e10)

  rejectDuration(
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )
  return {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  }
}

export function parseFromEnUsFormat (datetime) {
  const parts = datetime.split(/[^\w]+/)

  if (parts.length !== 7) {
    throw new RangeError(`expected 7 parts in "${datetime}`)
  }

  const month = +parts[0]
  const day = +parts[1]
  let year = +parts[2]
  const era = parts[3].toUpperCase()
  if (era === 'B' || era === 'BC') {
    year = -year + 1
  } else if (era !== 'A' && era !== 'AD') {
    throw new RangeError(`Unknown era ${era} in "${datetime}`)
  }
  let hour = +parts[4]
  if (hour === 24) {
    // bugs.chromium.org/p/chromium/issues/detail?id=1045791
    hour = 0
  }
  const minute = +parts[5]
  const second = +parts[6]

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    throw new RangeError(`Invalid number in "${datetime}`)
  }

  return { year, month, day, hour, minute, second }
}
